import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import SportsSoccerRoundedIcon from "@mui/icons-material/SportsSoccerRounded";
import LeaderboardRoundedIcon from "@mui/icons-material/LeaderboardRounded";
import EmojiEventsRoundedIcon from "@mui/icons-material/EmojiEventsRounded";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import PlayerCard from "../../components/PlayerCard/PlayerCard";
import AppLayout from "../../components/ui/AppLayout";
import PageHeader from "../../components/ui/PageHeader";
import TeamCrest from "../../components/ui/TeamCrest";
import LoadingState from "../../components/ui/LoadingState";
import EmptyState from "../../components/ui/EmptyState";
import api from "../../api";

// Caches próprios da página inicial: ela mostra o torneio mais recente com jogos, que pode não ser o ativo
// (TORNEIO_ID e jogadoresCache ficam para a votação e o admin, sempre do torneio ativo)
const CACHE_KEY = "home_timesCache";
const CACHE_KEY_TORNEIO = "home_torneio";
const CACHE_EXPIRATION = 1 * 24 * 60 * 60 * 1000; // 1 dia em milissegundos

const TimesPage = ({ embedded = false }) => {
  const navigate = useNavigate();
  const [times, setTimes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [torneioActive, setTorneioActive] = useState({});

  useEffect(() => {
    // Verifique se há um cache existente e se ele ainda é válido
    const cachedData = localStorage.getItem(CACHE_KEY);
    const cachedTimestamp = localStorage.getItem(`${CACHE_KEY}_timestamp`);
    const cachedTorneio = localStorage.getItem(CACHE_KEY_TORNEIO);

    if (cachedData && cachedTimestamp) {
      const currentTime = new Date().getTime();
      if (currentTime - parseInt(cachedTimestamp, 10) <= CACHE_EXPIRATION) {
        setTimes(JSON.parse(cachedData));
        if (cachedTorneio) setTorneioActive(JSON.parse(cachedTorneio));
        setLoading(false);
        return;
      }
    }

    api
      .get("/getTorneioComJogos")
      .then((torneio) => {
        setTorneioActive(torneio.data || {});
        if (!torneio.data) {
          setLoading(false);
          return;
        }
        return api
          .post("/report/getTimes", { torneioId: torneio.data._id })
          .then((response) => {
            const timesData = [];
            for (const nomeTime in response.data) {
              timesData.push({
                nomeTime: nomeTime,
                jogadores: response.data[nomeTime].jogadores,
                score: response.data[nomeTime].score,
              });
            }
            timesData.sort((a, b) => b.score - a.score);
            setTimes(timesData);
            setLoading(false);

            localStorage.setItem(CACHE_KEY, JSON.stringify(timesData));
            localStorage.setItem(`${CACHE_KEY}_timestamp`, new Date().getTime().toString());
            localStorage.setItem(CACHE_KEY_TORNEIO, JSON.stringify(torneio.data));
          });
      })
      .catch((error) => {
        console.error("Erro ao obter times:", error);
        setLoading(false);
      });
  }, []);

  const actions = (
    <>
      <Button variant="contained" startIcon={<SportsSoccerRoundedIcon />} onClick={() => navigate("/jogos")}>Jogos</Button>
      <Button variant="outlined" startIcon={<LeaderboardRoundedIcon />} onClick={() => navigate("/classificacaoView")}>Classificação</Button>
      <Button variant="outlined" startIcon={<EmojiEventsRoundedIcon />} onClick={() => navigate("/artilheiro")}>Artilharia</Button>
      <Button variant="outlined" startIcon={<StarRoundedIcon />} onClick={() => navigate("/melhorJogador")}>Melhor jogador</Button>
    </>
  );

  const content = (
    <>
      <PageHeader eyebrow="Torneio" title={torneioActive.nome || "Times do torneio"} subtitle="Elencos formados a partir da votação dos potes." actions={actions} />
      {loading ? (
        <LoadingState label="Carregando times..." />
      ) : times.length === 0 ? (
        <Card><EmptyState title="Nenhum time formado ainda" description="Os elencos aparecem aqui depois da formação dos potes." /></Card>
      ) : (
        <Stack spacing={3}>
          {times.map((time, index) => (
            <Card key={time.nomeTime} sx={{ overflow: "hidden" }}>
              <Stack direction={{ xs: "column", sm: "row" }} alignItems={{ sm: "center" }} spacing={2} sx={{ p: { xs: 2, sm: 3 }, borderBottom: 1, borderColor: "divider" }}>
                <TeamCrest name={time.nomeTime} size={64} />
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="h5">{time.nomeTime}</Typography>
                  <Typography variant="body2" color="text.secondary">{time.jogadores.length} jogadores</Typography>
                </Box>
                <Stack direction="row" spacing={1}>
                  <Chip label={`#${index + 1} em força`} variant="outlined" />
                  <Chip color="primary" label={`Força ${Number(time.score).toFixed(2)}`} />
                </Stack>
              </Stack>
              <Box sx={{ display: "flex", gap: 2, overflowX: "auto", p: { xs: 2, sm: 3 }, scrollSnapType: "x mandatory", "& > *": { scrollSnapAlign: "start" } }}>
                {time.jogadores.map((jogador) => (
                  <PlayerCard
                    key={jogador._id}
                    playerName={jogador.nome}
                    tower={jogador.user != null ? jogador.user.torre : ""}
                    ranking={jogador.ranking}
                    pote={jogador.pote}
                    shirtNumber={jogador.numeroCamisa}
                    playerImage={jogador.foto || (jogador.user && jogador.user.foto)}
                    teamName={time.nomeTime}
                    paisImage={`/bandeira_${jogador.pais}.png`}
                  />
                ))}
              </Box>
            </Card>
          ))}
        </Stack>
      )}
    </>
  );

  if (embedded) return content;
  return <AppLayout>{content}</AppLayout>;
};

export default TimesPage;
